import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import type { ClientWithRelations, Project, Facture, Devis, User } from '@/types/database-real'

export interface ClientMetrics {
  totalRevenue: number
  unpaidInvoices: number
  unpaidAmount: number
  activeProjects: number
  pendingQuotes: number
  acceptedQuotes: number
  totalQuotes: number
}

export function useClientDetail(clientId: string | undefined) {
  const [client, setClient] = useState<ClientWithRelations | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [factures, setFactures] = useState<Facture[]>([])
  const [devis, setDevis] = useState<Devis[]>([])
  const [assignedAdmins, setAssignedAdmins] = useState<User[]>([])
  const [metrics, setMetrics] = useState<ClientMetrics>({
    totalRevenue: 0,
    unpaidInvoices: 0,
    unpaidAmount: 0,
    activeProjects: 0,
    pendingQuotes: 0,
    acceptedQuotes: 0,
    totalQuotes: 0,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!clientId) {
      setLoading(false)
      return
    }

    async function fetchClientDetail() {
      try {
        setLoading(true)
        setError(null)

        // 1. Récupérer les données client de base
        const { data: clientData, error: clientError } = await supabase
          .from('users')
          .select('*')
          .eq('id', clientId)
          .eq('role', 'client')
          .single()

        if (clientError) throw clientError
        if (!clientData) {
          throw new Error('Client non trouvé')
        }

        // 2. Récupérer les projets (ATTENTION: projects utilise user_id)
        const { data: projectsData, error: projectsError } = await supabase
          .from('projects')
          .select('*')
          .eq('user_id', clientData.id)
          .order('created_at', { ascending: false })

        if (projectsError) throw projectsError

        // 3. Récupérer les factures (ATTENTION: facture utilise utilisateur_id)
        const { data: facturesData, error: facturesError } = await supabase
          .from('facture')
          .select('*')
          .eq('utilisateur_id', clientData.id)
          .order('created_at', { ascending: false })

        if (facturesError) throw facturesError

        // 4. Récupérer les devis (devis utilise client_id)
        const { data: devisData, error: devisError } = await supabase
          .from('devis')
          .select('*')
          .eq('client_id', clientData.id)
          .order('created_at', { ascending: false })

        if (devisError) throw devisError

        // 5. Récupérer les admins assignés (en 2 requêtes séparées)
        const { data: assignmentsData, error: assignmentsError } = await supabase
          .from('client_admin_assignments')
          .select('*')
          .eq('client_id', clientData.id)

        if (assignmentsError) throw assignmentsError

        // Récupérer les infos des admins
        const admins: User[] = []
        if (assignmentsData && assignmentsData.length > 0) {
          const adminIds = assignmentsData.map((a) => a.admin_id)
          const { data: adminsData, error: adminsError } = await supabase
            .from('users')
            .select('*')
            .in('id', adminIds)
            .eq('role', 'admin')

          if (!adminsError && adminsData) {
            admins.push(...adminsData)
          }
        }

        // 6. Calculer les métriques
        const totalRevenue = (facturesData || []).reduce(
          (sum, facture) => sum + (facture.total_ttc || 0),
          0
        )

        const unpaidInvoices = (facturesData || []).filter(
          (facture) => facture.statut === 'sent' || facture.statut === 'draft' || facture.statut === 'overdue'
        ).length

        const unpaidAmount = (facturesData || [])
          .filter((facture) => facture.statut === 'sent' || facture.statut === 'draft' || facture.statut === 'overdue')
          .reduce((sum, facture) => sum + (facture.total_ttc || 0), 0)

        const activeProjects = (projectsData || []).filter(
          (project) => project.status === 'in_progress' || project.status === 'pending'
        ).length

        const pendingQuotes = (devisData || []).filter(
          (d) => d.status === 'sent' || d.status === 'viewed'
        ).length

        const acceptedQuotes = (devisData || []).filter(
          (d) => d.status === 'accepted'
        ).length

        const totalQuotes = (devisData || []).length

        // Créer l'objet client étendu
        const clientWithRelations: ClientWithRelations = {
          ...clientData,
          projects: projectsData || [],
          factures: facturesData || [],
          devis: devisData || [],
          projects_count: projectsData?.length || 0,
          factures_count: facturesData?.length || 0,
          factures_total: totalRevenue,
          factures_unpaid: unpaidAmount,
          devis_count: devisData?.length || 0,
        }

        setClient(clientWithRelations)
        setProjects(projectsData || [])
        setFactures(facturesData || [])
        setDevis(devisData || [])
        setAssignedAdmins(admins)
        setMetrics({
          totalRevenue,
          unpaidInvoices,
          unpaidAmount,
          activeProjects,
          pendingQuotes,
          acceptedQuotes,
          totalQuotes,
        })
      } catch (err) {
        console.error('Error fetching client detail:', err)
        setError(err instanceof Error ? err.message : 'Une erreur est survenue')
      } finally {
        setLoading(false)
      }
    }

    fetchClientDetail()
  }, [clientId])

  return {
    client,
    projects,
    factures,
    devis,
    assignedAdmins,
    metrics,
    loading,
    error,
  }
}
